import * as PIXI from 'pixi.js';
import { PuzzleGenerator } from './PuzzleGenerator.js';
import { Piece } from './Piece.js';
import { SnapSystem } from './SnapSystem.js';
import { globalEventBus, GameEvents } from '../core/EventBus.js';

/**
 * 拼图板管理器
 * 管理所有拼图块的生成、布局和状态
 */
export class PuzzleBoard {
    constructor(app, config) {
        this.app = app;
        this.config = config;

        // 拼图生成器
        this.generator = new PuzzleGenerator({
            tabSize: config.puzzle.tabSize,
            jitter: config.puzzle.jitter,
            seed: config.puzzle.seed
        });

        // 吸附系统
        this.snapSystem = new SnapSystem(config.snap);

        // 拼图块容器
        this.container = new PIXI.Container();
        
        // 背景预览容器 (在拼图块下方)
        this.bgContainer = new PIXI.Container();
        this.app.stage.addChild(this.bgContainer);
        this.app.stage.addChild(this.container);

        // 预览 Sprite
        this.previewSprite = null;

        // 所有拼图块
        this.pieces = [];
        
        // 逻辑坐标映射表 (优化吸附检测)
        this.pieceMap = new Map(); // "col,row" -> Piece

        // 碎片组（已连接的碎片集合）
        // 每个组是一个 Set<Piece>，组内的碎片会一起移动
        this.pieceGroups = new Map(); // piece -> group (Set)

        // 当前配置
        this.cols = 0;
        this.rows = 0;
        this.pieceSize = 0;

        // 图片纹理
        this.imageTexture = null;

        // 拼图板背景（深色区域）
        this.boardBackground = null;

        // 主题配置
        // 格式: { side: 左右区域颜色, center: 中间区域颜色 }
        this.themes = [
            { name: 'Default', side: 0xB8A898, center: 0x7A6B5A },
            { name: 'Dark Blue', side: 0x57658A, center: 0x252629 },
            { name: 'Fresh Blue', side: 0xDCEFF6, center: 0xA8D8E4 },
            { name: 'Vintage Wood', side: 0xA47864, center: 0x392212 },
            { name: 'Minimalist', side: 0xE9E6D7, center: 0x2B313F },
            { name: 'color5', side: 0xE4E9EC, center: 0x598BB0 }
        ];
        this.currentThemeIndex = 0;

        // 事件总线
        this.events = globalEventBus;
    }

    /**
     * 设置当前主题
     * @param {number} index 主题索引
     */
    setTheme(index) {
        if (index >= 0 && index < this.themes.length) {
            this.currentThemeIndex = index;
            // 立即重绘背景
            if (this.boardBackground) {
                this.drawBackgroundParts();
            }
            console.log(`Theme switched to: ${this.themes[index].name}`);
        }
    }

    /**
     * 切换到下一个主题
     */
    nextTheme() {
        const nextIndex = (this.currentThemeIndex + 1) % this.themes.length;
        this.setTheme(nextIndex);
    }

    /**
     * 生成拼图
     * @param {Object} options 配置选项
     * @param {PIXI.Texture} imageTexture 可选的图片纹理
     */
    async generate(options = {}, imageTexture = null) {
        // 清除现有拼图
        this.clear();

        this.imageTexture = imageTexture;

        // 1. 获取用户期望的配置
        let targetCols = options.cols || this.config.puzzle.cols;
        let targetRows = options.rows || this.config.puzzle.rows;
        const targetPieceCount = targetCols * targetRows;

        // 2. 自适应网格计算 (Adaptive Grid)
        // 枚举目标总数的所有因数对，选择与图片宽高比最接近的组合
        // 保证 cols × rows === targetPieceCount，不会出现碎片数量偏差
        if (this.imageTexture) {
            const imgRatio = this.imageTexture.width / this.imageTexture.height;

            // 找出所有 cols × rows = targetPieceCount 且 cols >= 2, rows >= 2 的因数对
            const candidates = [];
            for (let c = 2; c * c <= targetPieceCount; c++) {
                if (targetPieceCount % c === 0) {
                    const r = targetPieceCount / c;
                    candidates.push({ cols: r, rows: c }); // 宽版
                    if (c !== r) {
                        candidates.push({ cols: c, rows: r }); // 高版
                    }
                }
            }

            if (candidates.length > 0) {
                let best = candidates[0];
                let bestDiff = Math.abs(best.cols / best.rows - imgRatio);
                for (let i = 1; i < candidates.length; i++) {
                    const diff = Math.abs(candidates[i].cols / candidates[i].rows - imgRatio);
                    if (diff < bestDiff) {
                        bestDiff = diff;
                        best = candidates[i];
                    }
                }
                this.cols = best.cols;
                this.rows = best.rows;
            } else {
                // 无有效因数对（如质数），使用原始值
                this.cols = targetCols;
                this.rows = targetRows;
            }

            console.log(`Adaptive Grid: Target=${targetCols}x${targetRows}(${targetPieceCount}), ImgRatio=${imgRatio.toFixed(2)}. New Grid=${this.cols}x${this.rows}(${this.cols*this.rows})`);
        } else {
            // 如果没有图片（罕见），回退到默认
            this.cols = targetCols;
            this.rows = targetRows;
        }

        // 3. 自适应尺寸计算 (Adaptive Sizing)
        // 根据新确定的网格和屏幕空间，计算最大的碎片尺寸
        const { centerZone } = this.getLayoutParams();
        const targetWidth = centerZone.width * 0.9;
        const targetHeight = centerZone.height * 0.9;

        const bestSizeW = targetWidth / this.cols;
        const bestSizeH = targetHeight / this.rows;
        
        // 向下取整，确保能完全放入
        this.pieceSize = Math.max(40, Math.floor(Math.min(bestSizeW, bestSizeH)));

        console.log(`Adaptive Sizing: Center=${centerZone.width}x${centerZone.height}. New PieceSize=${this.pieceSize}`);

        // 更新生成器配置
        this.generator.updateConfig({
            tabSize: options.tabSize || this.config.puzzle.tabSize,
            jitter: options.jitter || this.config.puzzle.jitter,
            seed: options.seed || this.config.puzzle.seed
        });

        // 生成边缘数据
        this.generator.generate(this.cols, this.rows);

        // 创建拼图板深色背景
        this.createBoardBackground();

        // 创建预览图
        this.createPreviewSprite();

        // 创建拼图块
        this.createPieces();

        // 计算正确位置
        this.calculateCorrectPositions();

        this.events.emit(GameEvents.PUZZLE_GENERATED, {
            cols: this.cols,
            rows: this.rows,
            pieceCount: this.pieces.length
        });
    }

    /**
     * 获取游戏区域布局参数
     * @returns {Object} 布局参数
     */
    getLayoutParams() {
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;

        // 游戏区域高度占屏幕 80%，垂直居中
        const areaHeight = screenHeight * 0.8;
        const areaTop = screenHeight * 0.1; // 上方留白 10%

        console.log(`Layout: screenW=${screenWidth}, screenH=${screenHeight}, areaH=${areaHeight}, areaTop=${areaTop}`);

        // 中间深色区域宽度占屏幕 50%，水平居中 (1:2:1 黄金分割视觉平衡)
        const centerWidth = screenWidth * 0.5;
        const centerLeft = (screenWidth - centerWidth) / 2;

        // 左侧区域
        const leftZone = {
            x: 10,
            width: centerLeft - 20,
            top: areaTop,
            height: areaHeight
        };

        // 右侧区域
        const rightZone = {
            x: centerLeft + centerWidth + 10,
            width: screenWidth - centerLeft - centerWidth - 20,
            top: areaTop,
            height: areaHeight
        };

        // 中间区域
        const centerZone = {
            x: centerLeft,
            width: centerWidth,
            top: areaTop,
            height: areaHeight
        };

        return { leftZone, rightZone, centerZone, areaTop, areaHeight };
    }

    /**
     * 创建拼图板深色背景
     */
    createBoardBackground() {
        // 清除旧背景
        if (this.boardBackground) {
            this.bgContainer.removeChild(this.boardBackground);
            this.boardBackground.destroy();
            this.boardBackground = null;
        }

        this.boardBackground = new PIXI.Graphics();
        this.drawBackgroundParts();

        // 添加到背景容器最底层
        this.bgContainer.addChildAt(this.boardBackground, 0);

        console.log(`Background added, bgContainer children: ${this.bgContainer.children.length}`);
    }

    /**
     * 绘制背景的三个部分：上下留白和中间区域
     * @private
     */
    drawBackgroundParts() {
        if (!this.boardBackground) return;

        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const { centerZone, areaTop, areaHeight } = this.getLayoutParams();

        // 获取当前主题颜色
        const theme = this.themes[this.currentThemeIndex];

        this.boardBackground.clear();

        // 移动端：统一使用中间区域颜色，不做左中右区分
        const isMobile = 'ontouchstart' in window && window.innerWidth <= 1024;
        if (isMobile) {
            this.boardBackground.rect(0, areaTop, screenWidth, areaHeight)
                .fill({ color: theme.center, alpha: 1.0 });
        } else {
            // 1. 绘制左右两侧的碎片存放区 (Side Zone Color)
            this.boardBackground.rect(0, areaTop, screenWidth, areaHeight)
                .fill({ color: theme.side, alpha: 1.0 });

            // 2. 绘制中间拼图工作区 (Center Zone Color)
            this.boardBackground.rect(centerZone.x, centerZone.top, centerZone.width, centerZone.height)
                .fill({ color: theme.center, alpha: 1.0 });
        }
    }

    /**
     * 创建预览图 Sprite
     */
    createPreviewSprite() {
        if (this.previewSprite) {
            this.bgContainer.removeChild(this.previewSprite);
            this.previewSprite.destroy();
        }

        if (!this.imageTexture) return;

        this.previewSprite = new PIXI.Sprite(this.imageTexture);
        
        // 设置尺寸
        const totalWidth = this.cols * this.pieceSize;
        const totalHeight = this.rows * this.pieceSize;
        this.previewSprite.width = totalWidth;
        this.previewSprite.height = totalHeight;
        
        // 设置位置 (居中)
        this.previewSprite.x = (this.app.screen.width - totalWidth) / 2;
        this.previewSprite.y = (this.app.screen.height - totalHeight) / 2;
        
        this.previewSprite.alpha = 0;
        this.previewSprite.visible = false;
        
        this.bgContainer.addChild(this.previewSprite);
    }

    /**
     * 动画显示预览图
     * @param {number} duration 持续时间
     * @param {number} fadeTime 淡入淡出时间
     */
    showPreviewAnimated(duration = 2000, fadeTime = 500) {
        if (!this.previewSprite) return;

        if (this.previewTicker) {
            this.app.ticker.remove(this.previewTicker);
        }

        this.previewSprite.visible = true;
        const startTime = Date.now();
        
        this.previewTicker = (ticker) => {
            const now = Date.now();
            const elapsed = now - startTime;

            if (elapsed < fadeTime) {
                // 淡入
                this.previewSprite.alpha = elapsed / fadeTime;
            } else if (elapsed < duration + fadeTime) {
                // 保持显示
                this.previewSprite.alpha = 1;
            } else if (elapsed < duration + fadeTime * 2) {
                // 淡出
                this.previewSprite.alpha = 1 - (elapsed - (duration + fadeTime)) / fadeTime;
            } else {
                // 结束
                this.previewSprite.alpha = 0;
                this.previewSprite.visible = false;
                this.app.ticker.remove(this.previewTicker);
                this.previewTicker = null;
            }
        };

        this.app.ticker.add(this.previewTicker);
    }

    /**
     * 根据碎片数量确定浮雕质量等级
     * @returns {string} 质量等级 'high' | 'low' | 'none'
     */
    determineBevelQuality() {
        const bevelConfig = this.config.bevel || {};
        const totalPieces = this.cols * this.rows;

        // 如果浮雕效果被禁用
        if (!bevelConfig.enabled) {
            return 'none';
        }

        const quality = bevelConfig.quality || 'auto';

        // 如果是自动模式，根据碎片数量决定
        if (quality === 'auto') {
            const threshold = bevelConfig.autoThreshold || 200;
            return totalPieces > threshold ? 'low' : 'high';
        }

        return quality;
    }

    /**
     * 创建所有拼图块
     */
    createPieces() {
        const tabSize = this.generator.getTabSize();

        // 确定浮雕质量等级
        const bevelQuality = this.determineBevelQuality();
        const bevelConfig = this.config.bevel || {};

        // 构建传递给Piece的浮雕配置
        const pieceBevelConfig = {
            quality: bevelQuality,
            shadowOffset: bevelConfig.shadowOffset || 3,
            shadowColor: bevelConfig.shadowColor || 0x000000,
            shadowAlpha: bevelConfig.shadowAlpha || 0.3,
            highlightColor: bevelConfig.highlightColor || 0xffffff,
            highlightAlpha: bevelConfig.highlightAlpha || 0.4,
            darkEdgeColor: bevelConfig.darkEdgeColor || 0x000000,
            darkEdgeAlpha: bevelConfig.darkEdgeAlpha || 0.2
        };

        console.log(`Creating ${this.cols * this.rows} pieces with bevel quality: ${bevelQuality}`);

        for (let row = 0; row < this.rows; row++) {
            for (let col = 0; col < this.cols; col++) {
                const { path, padding } = this.generator.generatePiecePath(
                    col, row, this.pieceSize
                );

                const pieceOptions = {
                    app: this.app,
                    col,
                    row,
                    size: this.pieceSize,
                    padding,
                    path,
                    texture: this.imageTexture,
                    pieceWidth: this.pieceSize,
                    pieceHeight: this.pieceSize,
                    totalCols: this.cols,
                    totalRows: this.rows,
                    bevelConfig: pieceBevelConfig
                };

                const piece = new Piece(pieceOptions);

                this.pieces.push(piece);
                this.pieceMap.set(`${col},${row}`, piece);
                this.container.addChild(piece.container);
            }
        }
        console.log(`PieceMap populated. Size: ${this.pieceMap.size}`);
    }

    /**
     * 计算每个拼图块的正确位置
     */
    calculateCorrectPositions() {
        const totalWidth = this.cols * this.pieceSize;
        const totalHeight = this.rows * this.pieceSize;
        
        const startX = (this.app.screen.width - totalWidth) / 2;
        const startY = (this.app.screen.height - totalHeight) / 2;

        for (const piece of this.pieces) {
            const correctX = startX + piece.col * this.pieceSize - piece.padding;
            const correctY = startY + piece.row * this.pieceSize - piece.padding;
            piece.setCorrectPosition(correctX, correctY);
        }
    }

    /**
     * 散落拼图块到左右两侧区域
     * 优化：使用网格分布算法避免碎片重叠
     */
    scatter() {
        const rotationEnabled = this.config.rotation && this.config.rotation.enabled;
        const { leftZone, rightZone } = this.getLayoutParams();

        // 将碎片分为两半
        const totalPieces = this.pieces.length;
        const leftCount = Math.ceil(totalPieces / 2);
        const rightCount = totalPieces - leftCount;

        const leftPieces = this.pieces.slice(0, leftCount);
        const rightPieces = this.pieces.slice(leftCount);

        // 辅助函数：在指定区域内均匀散落碎片
        const distributeInZone = (pieces, zone) => {
            if (pieces.length === 0) return;

            // 计算大概的网格行列数
            // 尝试让网格接近正方形或适配区域比例
            const zoneRatio = zone.width / zone.height;
            let cols = Math.ceil(Math.sqrt(pieces.length * zoneRatio));
            let rows = Math.ceil(pieces.length / cols);
            
            // 确保不会太挤
            // 碎片预估占用空间（稍微留点空隙）
            const slotWidth = zone.width / cols;
            const slotHeight = zone.height / rows;

            // 稍微打乱顺序
            const shuffled = [...pieces].sort(() => Math.random() - 0.5);

            for (let i = 0; i < shuffled.length; i++) {
                const piece = shuffled[i];
                const bounds = piece.getBounds(); // 获取实际尺寸
                
                // 计算网格坐标
                const col = i % cols;
                const row = Math.floor(i / cols);

                // 计算格子中心
                const centerX = zone.x + (col + 0.5) * slotWidth;
                const centerY = zone.top + (row + 0.5) * slotHeight;

                // 在格子内添加一点随机偏移 (jitter)，但不要超出格子太多
                const jitterX = (Math.random() - 0.5) * (slotWidth * 0.4);
                const jitterY = (Math.random() - 0.5) * (slotHeight * 0.4);

                let posX = centerX + jitterX - bounds.width / 2;
                let posY = centerY + jitterY - bounds.height / 2;
                
                // 边界检查：确保不超出区域
                posX = Math.max(zone.x, Math.min(posX, zone.x + zone.width - bounds.width));
                posY = Math.max(zone.top, Math.min(posY, zone.top + zone.height - bounds.height));

                piece.setPosition(posX, posY);

                if (rotationEnabled) {
                    const randomAngle = Math.floor(Math.random() * 4) * 90;
                    piece.setRotation(randomAngle);
                } else {
                    piece.setRotation(0);
                }
            }
        };

        // 分别散落到左右两侧
        distributeInZone(leftPieces, leftZone);
        distributeInZone(rightPieces, rightZone);

        this.events.emit(GameEvents.PUZZLE_SCATTERED);
    }

    /**
     * 将拼图排列成网格（调试用）
     */
    arrangeGrid() {
        for (const piece of this.pieces) {
            piece.setPosition(piece.correctX, piece.correctY);
            piece.setRotation(0);
        }
    }

    /**
     * 带动画排列网格
     * @param {number} duration 动画持续时间（毫秒）
     */
    arrangeGridAnimated(duration = 500) {
        const startTime = Date.now();

        for (const piece of this.pieces) {
            const startPos = piece.getPosition();
            const startRot = piece.rotation;

            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);

                // 缓动函数
                const eased = 1 - Math.pow(1 - progress, 3);

                const newX = startPos.x + (piece.correctX - startPos.x) * eased;
                const newY = startPos.y + (piece.correctY - startPos.y) * eased;
                const newRot = startRot * (1 - eased);

                piece.setPosition(newX, newY);
                piece.setRotation(newRot);

                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };

            animate();
        }
    }

    /**
     * 获取碎片组的边缘碎片（最左、最右、最上、最下）
     * 只有边缘碎片才可能与其他组吸附
     * @param {Set<Piece>} group 碎片组
     * @returns {Array<Piece>} 边缘碎片数组（去重）
     */
    getGroupEdgePieces(group) {
        if (group.size === 1) {
            return Array.from(group);
        }

        const edgePieces = [];
        for (const p of group) {
            const neighbors = [
                `${p.col+1},${p.row}`, `${p.col-1},${p.row}`,
                `${p.col},${p.row+1}`, `${p.col},${p.row-1}`
            ];
            const hasExternalNeighbor = neighbors.some(key => {
                const neighbor = this.pieceMap.get(key);
                return !neighbor || !group.has(neighbor);
            });
            if (hasExternalNeighbor) edgePieces.push(p);
        }
        return edgePieces;
    }

    /**
     * 检查拼图块是否可以吸附（包括到正确位置和相邻碎片）
     * 优化：只检查碎片组的边缘碎片，而不是遍历所有碎片
     * @param {Piece} piece 要检查的拼图块
     * @returns {boolean} 是否吸附成功
     */
    checkSnap(piece) {
        if (piece.isSnapped) return false;

        // 获取该碎片所在的组
        const group = this.getGroup(piece);

        // 获取组的边缘碎片（只有边缘碎片才可能与其他组吸附）
        const edgePieces = this.getGroupEdgePieces(group);

        // 动态阈值：碎片组越大，精确定位越困难，适当放宽吸附距离
        const baseThreshold = this.snapSystem.getThreshold();
        const dynamicThreshold = baseThreshold + Math.min(group.size - 1, 15);

        console.log(`[SnapDebug] checkSnap: group size=${group.size}, edgePieces=${edgePieces.map(p => `${p.col},${p.row}`).join(' | ')}, threshold=${dynamicThreshold}`);

        // 首先检查边缘碎片是否可以吸附到正确位置
        for (const p of edgePieces) {
            const shouldSnapToCorrect = this.snapSystem.checkSnap(p, dynamicThreshold);

            if (shouldSnapToCorrect) {
                // 吸附整个组到正确位置
                this.snapGroupToCorrectPosition(p, group);
                return true;
            }
        }

        // 然后检查边缘碎片是否可以与相邻碎片（不在同组内）吸附
        for (const p of edgePieces) {
            const neighborSnap = this.checkNeighborSnapExcludeGroup(p, group, dynamicThreshold);
            if (neighborSnap) {
                this.snapToNeighbor(p, group, neighborSnap);
                return true;
            }
        }

        return false;
    }

    /**
     * 检查碎片是否可以与不在同组内的相邻碎片吸附
     * @param {Piece} piece 当前碎片
     * @param {Set<Piece>} excludeGroup 要排除的碎片组
     * @returns {Object|null} 吸附信息或null
     */
    checkNeighborSnapExcludeGroup(piece, excludeGroup, thresholdOverride = null) {
        // console.log(`Checking snap. PieceSize: ${this.pieceSize}`);
        // 优化：不再过滤数组，而是直接传递 Map 和排除组，实现 O(1) 查找
        return this.snapSystem.checkNeighborSnap(piece, this.pieceMap, this.pieceSize, excludeGroup, thresholdOverride);
    }

    /**
     * 获取碎片所在的组
     * @param {Piece} piece 碎片
     * @returns {Set<Piece>} 碎片组
     */
    getGroup(piece) {
        if (this.pieceGroups.has(piece)) {
            return this.pieceGroups.get(piece);
        }
        // 如果碎片不在任何组中，创建一个只包含自己的组
        const newGroup = new Set([piece]);
        this.pieceGroups.set(piece, newGroup);
        return newGroup;
    }

    /**
     * 将整个组吸附到正确位置
     * @param {Piece} piece 触发吸附的碎片
     * @param {Set<Piece>} group 碎片组
     */
    snapGroupToCorrectPosition(piece, group) {
        console.log(`Snapping group of ${group.size} pieces to correct position.`);
        
        // 计算偏移量
        const piecePos = piece.getPosition();
        const offsetX = piece.correctX - piecePos.x;
        const offsetY = piece.correctY - piecePos.y;

        // 记录波源中心 (以触发吸附的那个碎片为中心)
        // 注意：这里用的是移动后的位置（也就是正确位置）
        const centerX = piece.correctX;
        const centerY = piece.correctY;

        // 收集需要播放特效的碎片及其距离
        const piecesWithDist = [];

        // 移动组内所有碎片
        for (const p of group) {
            // 如果已经吸附，跳过（防止重复处理）
            if (p.isSnapped) continue;

            const pos = p.getPosition();
            // 更新位置
            const newX = pos.x + offsetX;
            const newY = pos.y + offsetY;
            
            p.setPosition(newX, newY);
            p.snap();
            
            // 计算距离用于特效
            const dx = newX - centerX;
            const dy = newY - centerY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            piecesWithDist.push({ piece: p, dist: dist });
        }

        // 排序并限制数量 (最大10个)
        piecesWithDist.sort((a, b) => a.dist - b.dist);
        
        const MAX_EFFECT_PIECES = 10;
        const count = Math.min(piecesWithDist.length, MAX_EFFECT_PIECES);

        for (let i = 0; i < count; i++) {
            const item = piecesWithDist[i];
            const delay = item.dist * 0.5;
            item.piece.playSnapEffect(delay);
        }
        
        // 关键修复：在批量吸附完成后，强制检查一次游戏完成状态
        // 防止循环中的单次事件触发时，整体状态尚未完全更新导致检查失败
        // 我们通过发送一个自定义事件或直接调用 Game 的检查方法（如果能访问）
        // 但这里最好的方式是依赖 EventBus，只需确保最后的状态是同步的
        
        // 额外发送一个 "BATCH_SNAP_COMPLETE" 或类似的信号，或者依靠最后一次 p.snap() 的事件
        // 但为了保险，我们可以手动触发一次检查逻辑（如果 Game 监听了）
        // 由于 Game 监听的是 PIECE_SNAP，我们在循环结束后，确保再次触发检查
        // 可以通过 EventBus 发送一个特殊事件，或者只是依靠循环中最后一个 snap。
        
        // 增加日志以辅助调试
        const unsnapped = this.getUnsnappedPieces().length;
        console.log(`Group snapped. Remaining unsnapped pieces: ${unsnapped}`);
        
        if (unsnapped === 0) {
            // 确保触发完成
            // 有时事件队列可能会导致竞态，这里延迟一帧确保状态已更新
            setTimeout(() => {
                this.events.emit(GameEvents.PIECE_SNAP, piece); 
            }, 0);
        }
    }

    /**
     * 隐藏组内所有碎片的阴影
     * @param {Set<Piece>} group 碎片组
     */
    hideGroupShadows(group) {
        for (const piece of group) {
            piece.hideShadow();
        }
    }

    /**
     * 按照 row/col 顺序重新排列组内碎片的层级
     * 左上角的碎片在底层，右下角的碎片在顶层
     * @param {Set<Piece>} group 碎片组
     */
    reorderGroupZIndex(group) {
        // 将组转换为数组并按 row, col 排序
        const sortedPieces = Array.from(group).sort((a, b) => {
            // 先按 row 排序（小的在前）
            if (a.row !== b.row) {
                return a.row - b.row;
            }
            // row 相同时按 col 排序（小的在前）
            return a.col - b.col;
        });

        // 按排序顺序重新添加到容器，实现正确的层级
        for (const piece of sortedPieces) {
            // 将碎片移到容器顶层，这样排序后的顺序就是：
            // 最后添加的（右下角）在最顶层
            this.container.addChild(piece.container);
        }
    }

    /**
     * 合并两个碎片组
     * @param {Set<Piece>} group1 组1
     * @param {Set<Piece>} group2 组2
     */
    mergeGroups(group1, group2) {
        if (group1 === group2) return;

        // 将 group2 的所有碎片添加到 group1
        for (const piece of group2) {
            group1.add(piece);
            this.pieceGroups.set(piece, group1);
        }
    }

    /**
     * 检查组内的碎片是否都在正确位置
     * @param {Set<Piece>} group 碎片组
     */
    checkGroupComplete(group) {
        // 检查组内是否有任何碎片在正确位置
        for (const piece of group) {
            if (piece.isNearCorrectPosition(this.snapSystem.getThreshold())) {
                // 如果有一个在正确位置，整个组都吸附
                this.snapGroupToCorrectPosition(piece, group);
                return;
            }
        }
    }

    /**
     * 移动整个碎片组
     * @param {Piece} piece 被拖拽的碎片
     * @param {number} deltaX X方向移动量
     * @param {number} deltaY Y方向移动量
     */
    moveGroup(piece, deltaX, deltaY) {
        const group = this.getGroup(piece);
        for (const p of group) {
            const pos = p.getPosition();
            p.setPosition(pos.x + deltaX, pos.y + deltaY);
        }
    }

    /**
     * 将整个碎片组置顶
     * @param {Piece} piece 碎片
     */
    bringGroupToFront(piece) {
        const group = this.getGroup(piece);
        for (const p of group) {
            p.bringToFront();
        }
    }

    /**
     * 检查拼图是否完成
     * @returns {boolean} 是否完成
     */
    isComplete() {
        // 条件1: 所有碎片都吸附到了正确位置（经典模式）
        const allSnapped = this.pieces.every(piece => piece.isSnapped);
        if (allSnapped) return true;

        // 条件2: 所有碎片都连接在一起（自由模式）
        // 只需要检查是否所有碎片都在同一个组里
        if (this.pieces.length > 0) {
            const firstPiece = this.pieces[0];
            const group = this.getGroup(firstPiece);
            // 如果第一个碎片所在的组的大小等于总碎片数，说明所有碎片都在这个组里
            if (group && group.size === this.pieces.length) {
                return true;
            }
        }

        return false;
    }

    /**
     * 将碎片吸附到相邻碎片
     * @param {Piece} piece 当前碎片
     * @param {Set<Piece>} group 当前碎片的组
     * @param {Object} neighborSnap 吸附信息
     */
    snapToNeighbor(piece, group, neighborSnap) {
        const { targetPiece, snapX, snapY } = neighborSnap;

        // 计算需要移动的偏移量
        const piecePos = piece.getPosition();
        const offsetX = snapX - piecePos.x;
        const offsetY = snapY - piecePos.y;

        // 移动当前组的所有碎片
        for (const p of group) {
            const pos = p.getPosition();
            p.setPosition(pos.x + offsetX, pos.y + offsetY);
        }

        // 合并两个组
        const targetGroup = this.getGroup(targetPiece);
        this.mergeGroups(group, targetGroup);

        // 按照 row/col 顺序重新排列层级
        this.reorderGroupZIndex(group);

        // 隐藏组内所有碎片的阴影（只保留整体效果）
        this.hideGroupShadows(group);

        // 检查合并后的组是否所有碎片都在正确位置
        this.checkGroupComplete(group);

        // 播放波纹吸附特效 (Ripple Effect)
        // 1. 计算接触中心 (波源)
        // 简单取两个触发吸附的碎片的中间点
        const p1Pos = piece.getPosition();
        const p2Pos = targetPiece.getPosition();
        const centerX = (p1Pos.x + p2Pos.x) / 2;
        const centerY = (p1Pos.y + p2Pos.y) / 2;

        // 2. 为组内每个碎片计算距离并收集
        const piecesWithDist = [];
        for (const p of group) {
            const pos = p.getPosition();
            const dx = pos.x - centerX;
            const dy = pos.y - centerY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            piecesWithDist.push({ piece: p, dist: dist });
        }

        // 3. 排序并限制数量 (最大10个)
        // 按照距离从小到大排序
        piecesWithDist.sort((a, b) => a.dist - b.dist);
        
        const MAX_EFFECT_PIECES = 10;
        const count = Math.min(piecesWithDist.length, MAX_EFFECT_PIECES);

        for (let i = 0; i < count; i++) {
            const item = piecesWithDist[i];
            // 距离越远，延迟越久。系数 0.5ms/pixel
            const delay = item.dist * 0.5; 
            item.piece.playSnapEffect(delay);
        }

        // 发送吸附事件，这也将触发 Game 中的 checkCompletion
        // 注意：这里复用了 PIECE_SNAP 事件，虽然碎片可能没有 snap 到绝对位置
        // 但对于游戏进程来说，这是一次成功的连接
        this.events.emit(GameEvents.PIECE_SNAP, piece);
    }

    /**
     * 获取完成进度
     * @returns {number} 进度百分比 0-100
     */
    getProgress() {
        if (this.pieces.length === 0) return 0;
        const snappedCount = this.pieces.filter(p => p.isSnapped).length;
        return Math.round((snappedCount / this.pieces.length) * 100);
    }

    /**
     * 获取指定位置的拼图块
     * @param {number} col 列
     * @param {number} row 行
     * @returns {Piece|null} 拼图块
     */
    getPieceAt(col, row) {
        return this.pieces.find(p => p.col === col && p.row === row) || null;
    }

    /**
     * 获取所有未吸附的拼图块
     * @returns {Array<Piece>} 未吸附的拼图块数组
     */
    getUnsnappedPieces() {
        return this.pieces.filter(p => !p.isSnapped);
    }

    /**
     * 获取拼图块数量
     * @returns {number} 数量
     */
    getPieceCount() {
        return this.pieces.length;
    }

    /**
     * 处理窗口大小变化
     */
    onResize() {
        if (this.pieces.length === 0) return;

        const totalWidth = this.cols * this.pieceSize;
        const totalHeight = this.rows * this.pieceSize;

        // 计算新的居中偏移量
        const startX = (this.app.screen.width - totalWidth) / 2;
        const startY = (this.app.screen.height - totalHeight) / 2;

        // 更新拼图板背景位置和尺寸
        if (this.boardBackground) {
            this.drawBackgroundParts();
        }

        // 更新预览图位置
        if (this.previewSprite) {
            this.previewSprite.x = startX;
            this.previewSprite.y = startY;
        }

        // 更新所有拼图块的正确位置
        for (const piece of this.pieces) {
            const correctX = startX + piece.col * this.pieceSize - piece.padding;
            const correctY = startY + piece.row * this.pieceSize - piece.padding;
            
            // 如果拼图已吸附，直接更新位置
            if (piece.isSnapped) {
                piece.setPosition(correctX, correctY);
            }
            
            // 更新正确位置属性
            piece.setCorrectPosition(correctX, correctY);
        }
    }

    /**
     * 清除所有拼图块
     */
    clear() {
        for (const piece of this.pieces) {
            piece.destroy();
        }
        this.pieces = [];
        this.pieceMap.clear();
        this.pieceGroups.clear();
        this.container.removeChildren();
    }

    /**
     * 销毁
     */
    destroy() {
        this.clear();
        this.app.stage.removeChild(this.container);
        this.container.destroy();
    }
}

export default PuzzleBoard;
